<template>
  <div
    v-if="editor"
    :data-testid="name ? `wysiwyg-${name}` : undefined"
    class="relative flex flex-row rounded-md border border-slate-300 shadow-sm"
    :class="{
      'pointer-events-none': disabled,
      'mt-2': label
    }"
  >
    <BaseLabelTop
      :target="name"
      :label="label"
      :disabled="disabled"
      :required="required"
    />

    <div
      class="flex flex-1 flex-col"
      :class="{
        'bg-gray-100': disabled
      }"
    >
      <div class="flex flex-row">
        <div
          v-if="editionMode && displayFormatButton"
          class="flex flex-1 shrink-0 flex-row gap-2 p-2 pb-0"
        >
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('bold') }"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <FontAwesomeIcon :icon="['far', 'bold']" size="sm" />
          </button>
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('italic') }"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <FontAwesomeIcon :icon="['far', 'italic']" size="sm" />
          </button>
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('underline') }"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <FontAwesomeIcon :icon="['far', 'underline']" size="sm" />
          </button>
          <WysiwygLinkPopover :editor="editor" />
          <button
            v-if="canAddImage"
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('image') }"
            @click="addNewImage"
          >
            <FontAwesomeIcon :icon="['far', 'image']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('strike') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <FontAwesomeIcon :icon="['fal', 'strikethrough']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('heading', { level: 1 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
          >
            <FontAwesomeIcon :icon="['fal', 'font']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('heading', { level: 2 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          >
            <FontAwesomeIcon :icon="['fal', 'font']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('heading', { level: 3 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          >
            <FontAwesomeIcon :icon="['fal', 'font']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('paragraph') }"
            @click="editor.commands.setParagraph()"
          >
            <FontAwesomeIcon :icon="['far', 'paragraph']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('bulletList') }"
            @click="editor.chain().focus().toggleBulletList().run()"
          >
            <FontAwesomeIcon :icon="['fal', 'list-ul']" size="sm" />
          </button>
          <button
            class="hidden rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('orderedList') }"
            @click="editor.chain().focus().toggleOrderedList().run()"
          >
            <FontAwesomeIcon :icon="['fal', 'list-ol']" size="xs" />
          </button>
        </div>
        <div v-if="$slots['cta']" class="shrink-0 p-2 pb-0">
          <slot name="cta" />
        </div>
      </div>
      <div v-if="editionMode" class="w-full flex-1 overflow-y-auto">
        <EditorContent
          ref="ed"
          :editor="editor"
          class="block w-full border-0 p-0 text-gray-900 placeholder-gray-500 focus:ring-0 sm:text-sm"
        />
      </div>
      <div
        v-else
        class="max-w-full flex-1 bg-gray-100 p-3"
        v-html="modelValue"
      ></div>
      <div v-if="$slots['bottom-cta']" class="shrink-0 p-2 pt-0">
        <slot name="bottom-cta" />
      </div>
    </div>
  </div>

  <ErrorContainer :error-key="name" :errors="errors" />

  <div v-if="imageFeedback" class="form-help-container">
    <div
      class="form-help"
      :class="
        imageFeedback.status === 'success' ? 'text-green-600' : 'text-red-600'
      "
    >
      {{ imageFeedback.message }}
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import tippy from 'tippy.js'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Underline from '@tiptap/extension-underline'
import Text from '@tiptap/extension-text'
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import MentionList from './components/MentionList.vue'
import WysiwygLinkPopover from './components/WysiwygLinkPopover.vue'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Color from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Link from '@tiptap/extension-link'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'
import { mapActions } from 'vuex'
import { isImageMimeType, planImageResize } from './wysiwygImage'
import { flattenPastedHtml, flattenPastedText } from './singleLinePaste'
import '@/assets/css/_wysiwyg.css'

const IMAGE_FEEDBACK_DURATION = 8000

const SingleLineKeymap = Extension.create({
  name: 'singleLineKeymap',
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
      'Shift-Enter': () => true
    }
  }
})

export default {
  name: 'BaseWysiwyg',
  components: {
    BaseLabelTop,
    ErrorContainer,
    FontAwesomeIcon,
    EditorContent,
    WysiwygLinkPopover
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    canAddImage: {
      type: Boolean,
      required: false,
      default: false
    },
    displayFormatButton: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Single-line mode (e.g. a mail subject): no Enter, Shift-Enter or hard break,
     * pasted text and HTML are flattened onto one line, and the minimum height is reduced.
     */
    singleLine: {
      type: Boolean,
      required: false,
      default: false
    },
    suggestionVariables: {
      type: Array,
      required: false,
      default: () => []
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Object,
      required: false,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: true
    },
    maxDimensions: {
      type: Number,
      required: false,
      default: 1000
    }
  },
  emits: ['update:modelValue', 'change', 'image:feedback'],
  data() {
    return {
      editor: null,
      hasBeenFocused: false,
      imageFeedback: null,
      imageFeedbackTimeout: null
    }
  },
  watch: {
    editionMode(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.initEditor()
        } else {
          this.editor?.destroy()
        }
      }
    },
    modelValue(newValue) {
      let html = this.editor.getHTML()
      if (html !== newValue) {
        this.editor.commands.setContent(newValue)
      }
    }
  },
  mounted() {
    if (this.editionMode) {
      this.initEditor()
    }
  },
  beforeUnmount() {
    clearTimeout(this.imageFeedbackTimeout)
    this.editor?.destroy()
  },
  methods: {
    ...mapActions({
      saveWysiwygImage: 'user/saveWysiwygImage'
    }),
    addNewImage() {
      /** @type {HTMLInputElement} */
      let input = document.body.querySelector('input#selector-img')

      if (!input) {
        input = document.createElement('input')
        input.id = 'selector-img'
        input.type = 'file'
        input.accept = 'image/*'
        input.style.visibility = 'hidden'
      }

      input.onchange = (evt) => {
        const file = evt.target.files?.[0]

        input.remove()

        if (!file) {
          return
        }

        if (!isImageMimeType(file.type)) {
          this.setImageFeedback(
            'error',
            this.$t('global.wysiwyg_image_invalid_type')
          )

          return
        }

        this.loadImageFromFile(file)
          .then((image) => this.resizeImageIfNeeded(image, file))
          .then((result) => this.uploadImage(result))
          .catch(() => {
            this.setImageFeedback(
              'error',
              this.$t('global.wysiwyg_image_unreadable')
            )
          })
      }

      document.body.appendChild(input)

      input.click()
    },
    /**
     * @param {File} file
     * @return {Promise<HTMLImageElement>}
     */
    loadImageFromFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onloadend = () => {
          const image = new window.Image()

          image.onload = () => resolve(image)
          image.onerror = () => reject(new Error('unreadable_image'))
          image.src = reader.result
        }

        reader.onerror = () => reject(reader.error ?? new Error('read_failed'))

        reader.readAsDataURL(file)
      })
    },
    /**
     * Downscales the image when one of its sides exceeds maxDimensions, always
     * keeping the original aspect ratio. Images that already fit — and vector
     * images, which scale on their own — are returned untouched.
     *
     * @param {HTMLImageElement} image
     * @param {File} file
     * @return {Promise<{file: File, resized: boolean, width: number, height: number}>}
     */
    resizeImageIfNeeded(image, file) {
      const plan = planImageResize({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
        type: file.type,
        name: file.name,
        maxDimension: this.maxDimensions
      })

      if (!plan.resized) {
        return Promise.resolve({
          file,
          resized: false,
          width: plan.width,
          height: plan.height
        })
      }

      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas')

        canvas.width = plan.width
        canvas.height = plan.height

        const context = canvas.getContext('2d')

        if (!context) {
          reject(new Error('canvas_unavailable'))

          return
        }

        context.drawImage(image, 0, 0, plan.width, plan.height)

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('resize_failed'))

            return
          }

          resolve({
            file: new File([blob], plan.name, { type: plan.type }),
            resized: true,
            width: plan.width,
            height: plan.height
          })
        }, plan.type)
      })
    },
    /**
     * @param {{file: File, resized: boolean, width: number, height: number}} payload
     * @return {Promise<void>}
     */
    uploadImage({ file, resized, width, height }) {
      return this.saveWysiwygImage({ file })
        .then((res) => {
          if (!res?.success || !res?.file_url) {
            this.setImageFeedback(
              'error',
              this.$t('global.wysiwyg_image_upload_failed')
            )

            return
          }

          this.editor.chain().focus().setImage({ src: res.file_url }).run()

          this.setImageFeedback(
            'success',
            resized
              ? this.$t('global.wysiwyg_image_resized', { width, height })
              : this.$t('global.wysiwyg_image_added')
          )
        })
        .catch(() => {
          this.setImageFeedback(
            'error',
            this.$t('global.wysiwyg_image_upload_failed')
          )
        })
    },
    /**
     * Single feedback channel: message under the field, matching toast, and an
     * event so the parent screen can react if it needs to.
     *
     * @param {'success'|'error'} status
     * @param {string} message
     */
    setImageFeedback(status, message) {
      clearTimeout(this.imageFeedbackTimeout)

      this.imageFeedback = { status, message }

      if (status === 'success') {
        this.$h.toastSuccess(message)
      } else {
        this.$h.toastDanger(message)
      }

      this.$emit('image:feedback', { status, message })

      this.imageFeedbackTimeout = setTimeout(() => {
        this.imageFeedback = null
      }, IMAGE_FEEDBACK_DURATION)
    },
    initEditor() {
      this.hasBeenFocused = false
      this.editor = new Editor({
        editorProps: {
          attributes: {
            class: [
              'overflow-y-auto overflow-x-hidden focus:outline-none focus:!border-0 !p-3 !w-full max-w-none',
              this.singleLine ? 'min-h-[2.5rem]' : 'min-h-[8rem]'
            ].join(' ')
          },
          ...(this.singleLine && {
            transformPastedText: flattenPastedText,
            transformPastedHTML: flattenPastedHtml
          })
        },
        extensions: [
          StarterKit.configure({
            hardBreak: !this.singleLine,
            blockquote: !this.singleLine,
            codeBlock: !this.singleLine,
            horizontalRule: !this.singleLine,
            heading: false,
            paragraph: false,
            bulletList: false,
            orderedList: false
          }),
          Paragraph.extend({
            addAttributes() {
              return {
                ...this.parent?.(),
                style: {
                  default: 'margin:0; padding:0; line-height:2;'
                }
              }
            }
          }),
          Document,
          Paragraph,
          Underline,
          Text,

          TextStyle,
          Color,

          Link.configure({
            protocols: ['http', 'https', 'mailto'],
            openOnClick: false,
            HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' }
          }),

          Table.configure({
            resizable: false,
            HTMLAttributes: {
              border: 0,
              cellpadding: 0,
              cellspacing: 0,
              style: 'border-collapse:collapse;'
            }
          }),
          TableRow,
          TableCell,
          TableHeader,

          Image.configure({
            allowBase64: true,
            inline: true,
            addAttributes() {
              return {
                ...this.parent?.(),
                height: {
                  default: null,
                  parseHTML: (el) => el.getAttribute('height'),
                  renderHTML: (attrs) => ({ height: attrs.height })
                },
                width: {
                  default: null,
                  parseHTML: (el) => el.getAttribute('width'),
                  renderHTML: (attrs) => ({ width: attrs.width })
                }
              }
            },
            HTMLAttributes: {
              style: 'max-width:500px; margin:0; padding:0;'
            }
          }),
          Mention.configure({
            HTMLAttributes: {
              class: 'mention'
            },
            renderHTML({ options, node }) {
              return [
                'span',
                options.HTMLAttributes,
                `@${options.HTMLAttributes['data-label']}`
              ]
            },
            suggestion: {
              items: ({ query }) => {
                return this.suggestionVariables
                  .filter((model) => {
                    let search = query.length
                      ? this.$h.normalizeSearch(query)
                      : ''
                    if (
                      search.length < 2 ||
                      (model.name &&
                        this.$h.normalizeSearch(model.name).indexOf(search) ===
                          -1)
                    ) {
                      return false
                    } else {
                      return model.name
                    }
                  })
                  .slice(0, 10)
                  .reduce((init, item) => {
                    init.push(item)
                    return init
                  }, [])
              },
              render: () => {
                let component
                let popup
                return {
                  onStart: (props) => {
                    component = new VueRenderer(MentionList, {
                      props,
                      editor: props.editor
                    })
                    popup = tippy('body', {
                      getReferenceClientRect: props.clientRect,
                      appendTo: () => document.body,
                      content: component.element,
                      showOnCreate: true,
                      interactive: true,
                      trigger: 'manual',
                      placement: 'bottom-start'
                    })
                  },
                  onUpdate(props) {
                    component.updateProps(props)
                    popup[0].setProps({
                      getReferenceClientRect: props.clientRect
                    })
                  },
                  onKeyDown(props) {
                    if (component && component.ref) {
                      return component.ref.onKeyDown(props)
                    }
                  },
                  onExit() {
                    popup[0].destroy()
                    component.destroy()
                  }
                }
              }
            }
          }),
          ...(this.singleLine ? [SingleLineKeymap] : [])
        ],
        content: this.modelValue,
        onFocus: () => {
          this.hasBeenFocused = true
        },
        onUpdate: () => {
          let value = this.editor.getHTML()
          if (value.match(/^<[a-z]>( )*<\/[a-z]>$/i)) {
            value = ''
          }

          // value = value
          //   .replace(/<p><br><\/p>/gi, '')   // supprime paragraphes vides
          //   .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>') // réduit les doubles br

          this.$emit('update:modelValue', value)
          this.$emit('change', value)
        }
      })
    },
    setTextAtPosition(text) {
      let stringVar = `<span data-type="mention" class="mention" data-id="${text.value}" data-label="${text.description}">@${text.description}</span>`
      // Never focused yet: the selection sits at the start, so append the variable at the end instead.
      this.editor
        ?.chain()
        .focus(this.hasBeenFocused ? null : 'end')
        .insertContent(stringVar)
        .run()
    }
  }
}
</script>

<style>
.ProseMirror,
.ProseMirror.ProseMirror-focused {
  border: 0 !important;
}
</style>
